/**
 * Make a picture small before uploading it: a phone photo is 5-20 MB at 50 MP, while a profile
 * picture ends up 256 px and a room cover 960 px. Draws it at most `max` px on the long side as a
 * JPEG. Formats this browser can't draw (HEIC on Android) go up as they are; the server converts those.
 */
export async function shrinkImage(file: File, max: number): Promise<Blob> {
  try {
    const bmp = await createImageBitmap(file, { imageOrientation: 'from-image' });
    const scale = Math.min(1, max / Math.max(bmp.width, bmp.height));
    if (scale === 1 && file.size < 1_500_000 && /jpe?g|png|webp/i.test(file.type)) {
      bmp.close();
      return file; // already small
    }
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bmp.width * scale);
    canvas.height = Math.round(bmp.height * scale);
    canvas.getContext('2d')!.drawImage(bmp, 0, 0, canvas.width, canvas.height);
    bmp.close();
    const blob = await new Promise<Blob | null>((ok) => canvas.toBlob(ok, 'image/jpeg', 0.9));
    return blob ?? file;
  } catch {
    return file;
  }
}
